import { useMemo, useEffect } from 'react'
import React from 'react'
import PropTypes from 'prop-types'
import { Button, FormControlLabel, Checkbox, Typography } from '@mui/material'
import Stack from '@mui/material/Stack'
import { Field, Form, Formik } from 'formik'
import { TextField } from 'formik-mui'
import * as Yup from 'yup'
import { useTranslation } from 'react-i18next'
import {
  CheckboxControlContainer,
  CheckboxFieldContainer,
  StyledCheckboxCard,
} from '../../../styles'

/**
 * PaymentDetails Component
 *
 * A form component that collects bank account and payment information in the user edit workflow.
 * This component uses Formik for form validation and state management.
 *
 * @component
 * @param {Object} props - Component properties
 * @param {Object} props.payload - Initial payment data
 * @param {Object} [props.payload.paymentDetails] - Payment details information
 * @param {string} [props.payload.paymentDetails.creditInstituteName] - Name of the bank
 * @param {string} [props.payload.paymentDetails.bic] - BIC/SWIFT code
 * @param {string} [props.payload.paymentDetails.iban] - IBAN account number
 * @param {string} [props.payload.paymentDetails.accountHolderName] - Name of the account holder
 * @param {boolean} [props.payload.paymentDetails.accept] - Whether user accepted payment terms
 * @param {Function} props.setCanNext - Function to enable/disable the next button in parent stepper
 * @param {Function} props.setPayloadOne - Function to update the parent component's state with payment data
 * @param {Function} props.confirmPayload - Function to be called after successful form submission
 * @param {number} props.submit - Trigger for form submission from parent
 * @param {Function} props.setSubmit - Function to reset the submission trigger
 * @returns {React.ReactElement} The rendered form component
 */
function PaymentDetails({
  payload,
  setPayloadOne,
  confirmPayload,
  submit,
  setSubmit,
  setCanNext,
}) {
  const { t } = useTranslation()

  useEffect(() => {
    if (submit > 0) document.getElementById('subOne').click()
    setSubmit(0)
  }, [submit, setSubmit])

  const initialValues = {
    creditInstituteName: payload?.paymentDetails?.creditInstituteName || '',
    bic: payload?.paymentDetails?.bic || '',
    iban: payload?.paymentDetails?.iban || '',
    accountHolderName: payload?.paymentDetails?.accountHolderName || '',
    sepaAccepted: payload?.paymentDetails?.sepaAccepted || false,
  }

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        creditInstituteName: Yup.string()
          .required(t('LBLBankRequired'))
          .label(t('LBLBankName')),
        bic: Yup.string().required(t('LBLbicreq')).label(t('LBLBic')),
        iban: Yup.string().required(t('LBLibanReq')).label('LBLIban'),
        accountHolderName: Yup.string().label(t('LBLAccountHolder')),
        sepaAccepted: Yup.bool()
          .oneOf([true], t('LBLAcceptCondition'))
          .label('LBLPaymentAuthorization'),
      }),
    [t]
  )
  /** @type {import("formik").FormikConfig<typeof initialValues>['onSubmit']} */
  const handleSubmit = (values, actions) => {
    const payload = {
      creditInstituteName: values.creditInstituteName,
      bic: values.bic,
      iban: values.iban,
      accountHolderName: values.accountHolderName,
      sepaAccepted: values.sepaAccepted,
    }

    setPayloadOne(payload)
    confirmPayload()
    actions.setSubmitting(false)
    actions.setTouched({}, false)
    actions.setErrors({})
    actions.setFieldError({})
    actions.setFieldTouched({}, false, false)
  }

  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnMount={true}
        onSubmit={handleSubmit}
      >
        {({ touched, isValid, setFieldValue, values, errors }) => (
          <Form noValidate>
            <>
              {setCanNext(touched && isValid)}

              <Stack my={5} direction="row" alignItems="center" spacing={8}>
                <Field
                  fullWidth
                  margin="none"
                  component={TextField}
                  id="creditInstituteName"
                  name="creditInstituteName"
                  label={t('LBLBankName')}
                  required
                  InputProps={{
                    title: 'Bitte füllen Sie dieses Feld aus',
                  }}
                />
                <Field
                  fullWidth
                  margin="none"
                  component={TextField}
                  id="bic"
                  name="bic"
                  label={t('LBLBic')}
                  required
                  InputProps={{
                    title: 'Bitte füllen Sie dieses Feld aus',
                  }}
                />
              </Stack>
              <Stack mb={5} direction="row" spacing={8}>
                <Field
                  fullWidth
                  margin="none"
                  component={TextField}
                  id="iban"
                  name="iban"
                  label={t('LBLIban')}
                  required
                  InputProps={{
                    title: 'Bitte füllen Sie dieses Feld aus',
                  }}
                />
                <Field
                  fullWidth
                  margin="none"
                  component={TextField}
                  id="accountHolderName"
                  name="accountHolderName"
                  label={t('LBLAccountHolder')}
                  InputProps={{
                    title: 'Bitte füllen Sie dieses Feld aus',
                  }}
                />
              </Stack>
              {/* this is checkbox to skip the billing Address step in edit workglow. */}
              <Stack my={2} direction="row" alignItems="center" spacing={1}>
                <FormControlLabel
                  control={
                    <CheckboxControlContainer>
                      <CheckboxFieldContainer>
                        <Field
                          component={Checkbox}
                          type="checkbox"
                          id="sepaAccepted"
                          name="sepaAccepted"
                          onChange={(event) => {
                            setFieldValue('sepaAccepted', event.target.checked)
                          }}
                          checked={values.sepaAccepted}
                        />
                      </CheckboxFieldContainer>
                      <StyledCheckboxCard
                        variant="outline"
                        error={errors.sepaAccepted}
                      >
                        <Typography
                          sx={{ fontSize: 14 }}
                          color={
                            !errors.sepaAccepted
                              ? 'text.secondary'
                              : 'text.error'
                          }
                          gutterBottom
                        >
                          {t('LBLBankAccountAuthorizatoin')}
                        </Typography>
                        <Typography
                          variant="h5"
                          component="div"
                          color={
                            !errors.sepaAccepted ? 'text.primary' : 'text.error'
                          }
                        >
                          {t('LBLNote')}
                        </Typography>
                        <Typography
                          sx={{ fontSize: 14 }}
                          component="div"
                          color={
                            !errors.sepaAccepted ? 'text.primary' : 'text.error'
                          }
                        >
                          {t('LBLBankAccountNote')}
                        </Typography>
                        <Typography
                          sx={{ marginTop: 5 }}
                          variant="h5"
                          component="div"
                          color={
                            !errors.sepaAccepted ? 'text.primary' : 'text.error'
                          }
                        >
                          {t('LBLRequired')}
                        </Typography>
                      </StyledCheckboxCard>
                    </CheckboxControlContainer>
                  }
                />
              </Stack>

              <Button
                sx={{ display: 'none' }}
                id="subOne"
                type="submit"
              ></Button>
            </>
          </Form>
        )}
      </Formik>
    </>
  )
}
PaymentDetails.propTypes = {
  payload: PropTypes.objectOf(PropTypes.any),
  setPayloadOne: PropTypes.func.isRequired,
  confirmPayload: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
}

export default PaymentDetails
